<template>
  <div id="div-tabela-serv-comercial">
    <v-data-table
      class="data-table-serv-comercial"
      multi-sort
      dense
      :headers="headers"
      :items="items"
      :sort-by="['valor_total']"
      :sort-desc="[true]"
      :options="{
        itemsPerPage: 10,
      }"
      :headerProps="{
        sortByText: 'Ordenar Por',
      }"
      :hide-default-footer="items.length <= 10"
      no-data-text="Nenhum registro disponível"
      no-results-text="Nenhum registro correspondente encontrado"
      :mobile-breakpoint="10"
    >
      <template v-for="h in headers" v-slot:[`header.${h.value}`]="{ header }">
        <v-tooltip bottom v-if="traducaoIndicadores[h.value] != null">
          <template v-slot:activator="{ on }">
            <span v-on="on">{{ h.text }}</span>
          </template>
          <span>{{ traducaoIndicadores[h.value] }}</span>
        </v-tooltip>
        <span v-else>{{ h.text }}</span>
      </template>

      <template v-slot:[`item.valor_total`]="{ item }">
        <span :style="getColorStyle(item.valor_total)" class="no-wrap-currency">
          <template v-if="item.valor_total != null && item.valor_total !== 0">
            {{ item.valor_total | parseNumberToFloatBrIgnoringNaN }}&nbsp;R$
          </template>
          <template v-else>
            -
          </template>
        </span>
      </template>

      <template v-for="mes in mesesOrdenados" v-slot:[`item.${mes}`]="{ item }">
        <span :style="getColorStyle(item[mes])" class="no-wrap-currency">
          <template v-if="item[mes] != null && item[mes] !== 0">
            {{ item[mes] | parseNumberToFloatBrIgnoringNaN }}&nbsp;R$
          </template>
          <template v-else>
            -
          </template>
        </span>
      </template>
    </v-data-table>
  </div>
</template>

<script>
export default {
  name: "TableServComercial",
  props: {
    dadosServicosMensais: {
      type: Array,
      required: true,
    },
    traducaoIndicadores: {
      type: Object,
      default: {},
    },
  },

  computed: {
    mesesOrdenados() {
      if (
        !this.dadosServicosMensais ||
        this.dadosServicosMensais.length === 0
      ) {
        return [];
      }

      const meses = new Set();
      this.dadosServicosMensais.forEach((servico) => {
        Object.keys(servico).forEach((key) => {
          if (key.match(/^\d{2}\/\d{4}$/)) {
            meses.add(key);
          }
        });
      });

      return Array.from(meses).sort((a, b) => {
        const [mesA, anoA] = a.split("/");
        const [mesB, anoB] = b.split("/");
        const dataA = new Date(parseInt(anoA), parseInt(mesA) - 1);
        const dataB = new Date(parseInt(anoB), parseInt(mesB) - 1);
        return dataA - dataB;
      });
    },

    valoresMinMax() {
      if (
        !this.dadosServicosMensais ||
        this.dadosServicosMensais.length === 0
      ) {
        return { min: 0, max: 0 };
      }

      const valores = [];
      this.dadosServicosMensais.forEach((servico) => {
        this.mesesOrdenados.forEach((mes) => {
          const valor = parseFloat(servico[mes]);
          if (!isNaN(valor) && valor > 0) {
            valores.push(valor);
          }
        });

        const valorTotal = parseFloat(servico.valor_total);
        if (!isNaN(valorTotal) && valorTotal > 0) {
          valores.push(valorTotal);
        }
      });

      if (valores.length === 0) {
        return { min: 0, max: 0 };
      }

      return {
        min: Math.min(...valores),
        max: Math.max(...valores),
      };
    },
    headers() {
      const headers = [
        {
          text: "Serviço",
          value: "descricao",
          sortable: true,
          width: "200px",
        },
      ];

      this.mesesOrdenados.forEach((mes) => {
        headers.push({
          text: mes,
          value: mes,
          sortable: true,
          width: "80px",
        });
      });

      headers.push({
        text: "Total",
        value: "valor_total",
        sortable: true,
        width: "100px",
      });

      return headers;
    },
    items() {
      return this.dadosServicosMensais || [];
    },
  },

  methods: {
    hexToRgb(hex) {
      hex = hex.replace("#", "");

      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);

      return { r, g, b };
    },

    interpolateColor(valor) {
      const { min, max } = this.valoresMinMax;
      if (max === min || isNaN(valor) || valor <= 0) {
        return null;
      }
      const normalized = Math.max(0, Math.min(1, (valor - min) / (max - min)));
      if (normalized < 0.2) {
        return null;
      }
      const adjustedNormalized = (normalized - 0.2) / 0.8;
      const corPrimaryHex = this.$vuetify.theme.themes.light.primary;
      const corAlta = this.hexToRgb(corPrimaryHex);
      const corInicial = { r: 224, g: 204, b: 255 };
      const r = Math.round(
        corInicial.r + (corAlta.r - corInicial.r) * adjustedNormalized
      );
      const g = Math.round(
        corInicial.g + (corAlta.g - corInicial.g) * adjustedNormalized
      );
      const b = Math.round(
        corInicial.b + (corAlta.b - corInicial.b) * adjustedNormalized
      );

      return { r, g, b };
    },

    getColorStyle(valor) {
      if (valor == null || valor === 0) {
        return {
          padding: "4px 8px",
          fontWeight: "normal",
        };
      }

      const valorNumerico = parseFloat(valor);
      const cor = this.interpolateColor(valorNumerico);

      if (!cor) {
        return {
          padding: "4px 8px",
          fontWeight: valorNumerico > 0 ? "500" : "normal",
        };
      }

      const { r, g, b } = cor;

      const luminosidade = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      const corTexto = luminosidade > 0.5 ? "#000000" : "#FFFFFF";

      return {
        backgroundColor: `rgb(${r}, ${g}, ${b})`,
        color: corTexto,
        padding: "4px 8px",
        borderRadius: "3px",
        fontWeight: valorNumerico > 0 ? "500" : "normal",
      };
    },
  },
};
</script>

<style>
#div-tabela-serv-comercial {
  min-height: 420px;
}
.data-table-serv-comercial table tbody tr td {
  font-size: 12px;
  padding: 0px 8px !important;
}
.data-table-serv-comercial table thead tr th {
  font-size: 12px;
  height: 25px;
  font-weight: 400;
}

.no-wrap-currency {
  white-space: nowrap;
}
</style>
